import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../lib/auth";
import { OperationalShell } from "../../components/OperationalShell";
import { ActivityFeed } from "./ActivityFeed";
import { GuidanceTooltip } from "../../guidance/GuidanceTooltip";

/**
 * ActivityPulse — Phase 4 home page for the operational pulse channel
 * (and, for super-admins, an authority tab into the security channel).
 *
 * Tabs:
 *   ● Pulse    — operational events, plain language, scoped to org list
 *   ● Security — governance/compliance events, super-admin/compliance only
 *
 * The tab strip itself is hidden when the caller has no security access —
 * the absent tab disappears (L4 / L9: "Invisible means non-existent").
 */
const ActivityPulse = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const canSeeSecurity = ["super_admin", "compliance_admin", "governance_admin"].includes(user?.role);
    const [tab, setTab] = useState("pulse");

    return (
        <OperationalShell>
            <header className="mb-8 lg:mb-10 space-y-3" data-testid="ob-activity-header">
                <p className="ob-eyebrow">{t("ob.activity.eyebrow")}</p>
                <h1
                    className="text-4xl sm:text-5xl flex items-center"
                    style={{ color: "var(--ob-text-primary)" }}
                    data-testid="ob-activity-title"
                >
                    {t("ob.activity.title")}
                    <GuidanceTooltip guidanceKey="activity.intro" />
                </h1>
                <p
                    className="text-base max-w-2xl"
                    style={{ color: "var(--ob-text-secondary)" }}
                >
                    {t("ob.activity.subtitle")}
                </p>
            </header>

            {canSeeSecurity && (
                <nav
                    data-testid="ob-activity-tabs"
                    className="mb-8 flex items-center gap-1 p-1 rounded-full w-fit"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                    {[
                        { key: "pulse", label: t("ob.activity.tab_pulse") },
                        { key: "security", label: t("ob.activity.tab_security") },
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => setTab(opt.key)}
                            data-testid={`ob-activity-tab-${opt.key}`}
                            className="px-4 py-1.5 rounded-full text-sm transition-colors"
                            style={{
                                background: tab === opt.key ? "rgba(16,185,129,0.16)" : "transparent",
                                color: tab === opt.key ? "var(--ob-emerald)" : "var(--ob-text-secondary)",
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </nav>
            )}

            {tab === "pulse" && (
                <ActivityFeed
                    endpoint="/operational-base/activity"
                    limit={30}
                    emptyTitleKey="ob.activity.empty.pulse_title"
                    emptyHintKey="ob.activity.empty.pulse_hint"
                    data-testid="ob-pulse-feed"
                />
            )}
            {tab === "security" && canSeeSecurity && (
                <ActivityFeed
                    endpoint="/operational-base/activity/security"
                    limit={50}
                    emptyTitleKey="ob.activity.empty.security_title"
                    emptyHintKey="ob.activity.empty.security_hint"
                    data-testid="ob-security-feed"
                />
            )}
        </OperationalShell>
    );
};

export default ActivityPulse;
